import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useLiveContestRealtime from "hooks/useLiveContestRealtime";
import {
  useGetProductsQuery,
  useGetProductWishesQuery,
  useGetContestLiveStatsQuery,
  useGetLiveDashboardStatsQuery,
  useGetPlayTableHistoryQuery,
  useAnnounceProductWishResultMutation,
  useSetProductActiveStatusMutation,
  useSetProductPlayableStatusMutation,
  api,
} from "state/api";

const PAGE_SIZE = 50;

const isOngoing = (wish) => (wish?.status || "").toUpperCase() === "ONGOING";

/**
 * Shared live-dashboard session: products/wishes, realtime stats, history,
 * announce + product toggles. Used by desktop and mobile live pages.
 */
export function useLiveDashboardSession(productId, playToken) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedWish, setSelectedWish] = useState(null);
  const [liveRefreshTick, setLiveRefreshTick] = useState(0);
  const [liveRefreshing, setLiveRefreshing] = useState(false);
  const [liveEntryCount, setLiveEntryCount] = useState(null);
  const [liveUniqueUserCount, setLiveUniqueUserCount] = useState(null);
  const lastEntryCountRef = useRef(null);
  const lastUniqueUserCountRef = useRef(null);
  const lastFillWishRef = useRef(null);
  const historyRefetchTimerRef = useRef(null);

  const isLiveRound = Boolean(productId && playToken) && isOngoing(selectedWish);

  const { data: productsResponse, isLoading: productsLoading } = useGetProductsQuery();
  const products = productsResponse?.data || productsResponse?.products || productsResponse || [];

  const selectedProduct = useMemo(
    () => products.find((p) => p._id === productId) || null,
    [products, productId]
  );

  const {
    data: wishesData,
    isLoading: wishesLoading,
    refetch: refetchWishes,
  } = useGetProductWishesQuery(productId, {
    skip: !productId,
    refetchInterval: isLiveRound ? 5000 : false,
  });

  const wishes = wishesData?.productWishDocuments ?? [];

  const { data: liveStatsData } = useGetContestLiveStatsQuery(
    { productId, playToken },
    { skip: !isLiveRound, pollingInterval: 5_000 }
  );

  const {
    data: historyData,
    isLoading: historyLoading,
    refetch: refetchHistory,
  } = useGetPlayTableHistoryQuery(
    { productId, playToken, page: 1, pageSize: PAGE_SIZE },
    { skip: !productId || !playToken, pollingInterval: 10_000 }
  );

  const {
    data: dashboardStats,
    isLoading: dashboardStatsLoading,
    isFetching: dashboardStatsFetching,
    refetch: refetchDashboardStats,
  } = useGetLiveDashboardStatsQuery(
    { productId, playToken },
    {
      skip: !productId || !playToken,
      pollingInterval: 5_000,
    }
  );

  const [announceProductWishResult, { isLoading: announcingResult }] =
    useAnnounceProductWishResultMutation();
  const [setProductActiveStatus, { isLoading: togglingProductStatus }] =
    useSetProductActiveStatusMutation();
  const [setProductPlayableStatus, { isLoading: togglingPlayableStatus }] =
    useSetProductPlayableStatusMutation();

  const history = historyData?.history ?? [];
  const totalCount = liveEntryCount ?? historyData?.totalCount ?? 0;
  const uniqueUserCount =
    liveUniqueUserCount ??
    historyData?.uniqueUserCount ??
    selectedWish?.uniqueUserCount ??
    0;

  useEffect(() => {
    setLiveEntryCount(null);
    setLiveUniqueUserCount(null);
    lastEntryCountRef.current = null;
    lastUniqueUserCountRef.current = null;
    lastFillWishRef.current = null;
  }, [playToken, productId]);

  useEffect(
    () => () => {
      if (historyRefetchTimerRef.current) {
        clearTimeout(historyRefetchTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!productId || !playToken || wishes.length === 0) {
      setSelectedWish(null);
      return;
    }
    const fromApi = wishes.find((w) => w.playToken === playToken);
    if (!fromApi) {
      setSelectedWish(null);
      return;
    }
    setSelectedWish((prev) => {
      if (prev && prev._id === fromApi._id) {
        return {
          ...fromApi,
          fillWish: Math.max(fromApi.fillWish ?? 0, prev.fillWish ?? 0),
          entryCount: Math.max(fromApi.entryCount ?? 0, prev.entryCount ?? 0),
          uniqueUserCount: Math.max(
            fromApi.uniqueUserCount ?? 0,
            prev.uniqueUserCount ?? 0
          ),
        };
      }
      return fromApi;
    });
  }, [productId, playToken, wishes]);

  const scheduleLeaderboardRefetch = useCallback(
    (pid, token) => {
      if (historyRefetchTimerRef.current) {
        clearTimeout(historyRefetchTimerRef.current);
      }
      historyRefetchTimerRef.current = setTimeout(() => {
        dispatch(
          api.endpoints.getPlayTableHistory.initiate(
            { productId: pid, playToken: token, page: 1, pageSize: PAGE_SIZE },
            { forceRefetch: true }
          )
        );
      }, 800);
    },
    [dispatch]
  );

  const applyLiveStats = useCallback(
    (payload) => {
      if (!payload?.productId || !payload?.playToken) return;
      if (String(payload.productId) !== String(productId)) return;
      if (payload.message) return;

      if (
        lastFillWishRef.current !== null &&
        lastFillWishRef.current === payload.fillWish &&
        lastEntryCountRef.current === payload.entryCount &&
        lastUniqueUserCountRef.current === payload.uniqueUserCount
      ) {
        return;
      }

      setLiveEntryCount(payload.entryCount);
      setLiveUniqueUserCount(payload.uniqueUserCount ?? null);
      setSelectedWish((prev) => {
        if (!prev || prev.playToken !== payload.playToken) return prev;
        return {
          ...prev,
          fillWish: payload.fillWish,
          entryCount: payload.entryCount,
          uniqueUserCount: payload.uniqueUserCount ?? prev.uniqueUserCount,
          status: payload.status ?? prev.status,
        };
      });

      dispatch(
        api.util.updateQueryData("getProductWishes", productId, (draft) => {
          const wish = draft?.productWishDocuments?.find(
            (w) => w.playToken === payload.playToken
          );
          if (wish) {
            wish.fillWish = payload.fillWish;
            wish.entryCount = payload.entryCount;
            wish.uniqueUserCount = payload.uniqueUserCount ?? wish.uniqueUserCount;
            if (payload.status) wish.status = payload.status;
          }
        })
      );

      const entryCountChanged =
        lastEntryCountRef.current !== null &&
        payload.entryCount !== lastEntryCountRef.current;
      const uniqueUserCountChanged =
        lastUniqueUserCountRef.current !== null &&
        payload.uniqueUserCount !== lastUniqueUserCountRef.current;
      lastEntryCountRef.current = payload.entryCount;
      lastUniqueUserCountRef.current = payload.uniqueUserCount ?? null;
      lastFillWishRef.current = payload.fillWish;

      if (entryCountChanged || uniqueUserCountChanged) {
        scheduleLeaderboardRefetch(payload.productId, payload.playToken);
      }
    },
    [productId, dispatch, scheduleLeaderboardRefetch]
  );

  useEffect(() => {
    if (liveStatsData && !liveStatsData.message) {
      applyLiveStats(liveStatsData);
    }
  }, [liveStatsData, applyLiveStats]);

  const { connected: liveRealtimeConnected } = useLiveContestRealtime({
    productId,
    playToken,
    enabled: Boolean(productId && playToken),
    onUpdate: applyLiveStats,
  });

  const goBack = useCallback(() => {
    navigate("/prize-leaderboard", {
      state: { productId, playToken },
    });
  }, [navigate, productId, playToken]);

  const handleRefreshLiveData = useCallback(async () => {
    if (!productId || !playToken) return;
    setLiveRefreshing(true);
    try {
      await Promise.all([refetchWishes(), refetchHistory(), refetchDashboardStats()]);
      setLiveRefreshTick((tick) => tick + 1);
      toast.success("Data refreshed", { position: "top-right", autoClose: 1500 });
    } catch {
      toast.error("Refresh failed", { position: "top-right" });
    } finally {
      setLiveRefreshing(false);
    }
  }, [productId, playToken, refetchWishes, refetchHistory, refetchDashboardStats]);

  const handleToggleProductActive = useCallback(
    async (active) => {
      if (!productId) return;
      try {
        await setProductActiveStatus({ productId, active }).unwrap();
        toast.success(active ? "Contest is now active" : "Contest is now inactive", {
          position: "top-right",
        });
      } catch (err) {
        toast.error(err?.data?.message || "Failed to update contest status", {
          position: "top-right",
        });
      }
    },
    [productId, setProductActiveStatus]
  );

  const handleToggleProductPlayable = useCallback(
    async (playable) => {
      if (!productId) return;
      try {
        await setProductPlayableStatus({ productId, playable }).unwrap();
        toast.success(
          playable ? "Product is now playable (YES)" : "Product is now not playable (NO)",
          { position: "top-right" }
        );
      } catch (err) {
        toast.error(err?.data?.message || "Failed to update isPlayable", {
          position: "top-right",
        });
      }
    },
    [productId, setProductPlayableStatus]
  );

  const handleConfirmAnnounce = useCallback(async () => {
    if (!selectedWish?._id || !productId) return;
    try {
      const result = await announceProductWishResult({
        productWishId: selectedWish._id,
        productId,
      }).unwrap();
      toast.success(result?.message || "Result announced successfully", {
        position: "top-right",
      });
      setSelectedWish((prev) =>
        prev && prev._id === selectedWish._id ? { ...prev, status: "PROCESSED" } : prev
      );
      refetchWishes();
      refetchHistory();
      refetchDashboardStats();
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Failed to announce result", {
        position: "top-right",
      });
      throw err;
    }
  }, [
    selectedWish,
    productId,
    announceProductWishResult,
    refetchWishes,
    refetchHistory,
    refetchDashboardStats,
  ]);

  const productActive = (selectedProduct?.status || "").toUpperCase() === "ACTIVE";
  const productPlayable = (selectedProduct?.isPlayable || "").toUpperCase() === "YES";

  let gate = "ready";
  if (!productId || !playToken) gate = "invalid";
  else if (productsLoading || wishesLoading) gate = "loading";
  else if (!selectedProduct || !selectedWish) gate = "not_found";

  const historyForView =
    historyData?.message === "No matching play table history found" ? [] : history;

  return {
    gate,
    productId,
    playToken,
    selectedProduct,
    selectedWish,
    history: historyForView,
    totalCount,
    uniqueUserCount,
    historyLoading: historyLoading && !history.length,
    goBack,
    handleRefreshLiveData,
    liveRefreshing,
    liveRefreshTick,
    liveRealtimeConnected,
    dashboardStats,
    dashboardStatsLoading,
    refetchDashboardStats,
    dashboardStatsFetching,
    productActive,
    handleToggleProductActive,
    togglingProductStatus,
    productPlayable,
    handleToggleProductPlayable,
    togglingPlayableStatus,
    handleConfirmAnnounce,
    announcingResult,
    navigate,
  };
}

export default useLiveDashboardSession;
